// Übersicht nach dem Kampf (K8, L2): Die Spannung liegt in den Kosten.
// Wer kommt wie verletzt zurück, wer hat was geleistet, wer bekommt die XP, wer nimmt Abschied.
import { combatRules, getClass, getEnemy, getTrait } from '../content/index.ts';
import type { BattleResult } from '../sim/index.ts';
import { CLASS_COLORS } from './render.ts';

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const n = (x: number) => Math.round(x).toLocaleString('de-DE');

const HEADLINES: Record<BattleResult['outcome'], [string, string]> = {
  win: ['Das Ufer ist frei', 'Gewonnen. Die XP werden verteilt.'],
  retreat: ['Rückzug ans Boot', 'Die Rückzugsschwelle ist erreicht, alle laufen zum Boot. Aus dieser Begegnung gibt es nichts, auch keine XP.'],
  loss: ['Niederlage', 'Niemand steht mehr am Ufer. Aus dieser Begegnung gibt es nichts, auch keine XP.'],
};

export function summaryHtml(
  r: BattleResult,
  context: {
    lineup: string;
    storm: string;
    crits: Map<number, number>;
    againLabel?: string;
    extra?: string;
    fine?: string;
    kept?: string[];
    /** Im Run: die XP, die der Freund wirklich bekommt (ohne Item-Bonus, der steht bei den Balken) */
    xpOf?: (id: string | null) => number | undefined;
  },
): string {
  const [title, sub] = HEADLINES[r.outcome];
  const xpOf = (f: (typeof r.friends)[number]) => context.xpOf?.(f.id) ?? f.xp;
  const maxXp = Math.max(1, ...r.friends.map(xpOf));
  const rows = r.friends
    .map((f) => {
      const share = f.hpEnd / f.maxHp;
      const hpClass = f.farewell ? 'gone' : share < 0.34 ? 'low' : share < 0.67 ? 'mid' : 'high';
      // F15: Der Anteil eines Gegangenen geht an die Überlebenden
      const xpCell = f.farewell
        ? '–'
        : `<span class="xpbar"><span style="width:${(100 * xpOf(f)) / maxXp}%"></span></span><b>${n(xpOf(f))}</b>`;
      return `<tr class="${f.farewell ? 'farewell' : ''}" data-uid="${f.uid}">
        <th scope="row"><span class="dot" style="background:${CLASS_COLORS[f.friendClass]}"></span>${esc(f.name)}<small>${esc(getClass(f.friendClass).name)}</small></th>
        <td class="hp ${hpClass}">${f.farewell ? (f.id && context.kept?.includes(f.id) ? '<em class="ok">kommt wie versprochen nach</em>' : '<em>nimmt Abschied</em>') : `<span class="hpbar"><span style="width:${share * 100}%"></span></span>${n(f.hpEnd)} / ${n(f.maxHp)}${f.pushedOut ? ' <em class="hint">über den Rand geschubst</em>' : ''}${f.fell ? ' <em class="wound-note">✚ verwundet</em>' : ''}`}</td>
        <td>${n(f.stats.damage)}</td>
        <td>${n(f.stats.taken)}</td>
        <td>${n(f.stats.healed)}</td>
        <td>${n(f.stats.shielded)}</td>
        <td>${f.stats.kills}</td>
        <td>${context.crits.get(f.uid) ?? 0}</td>
        <td class="xp">${xpCell}</td>
      </tr>`;
    })
    .join('') + treeRows(r);
  // F22, Q43: Wer das Versprechen trägt, nimmt keinen Abschied, sondern kommt nach
  const gone = r.friends.filter((f) => f.farewell && !(f.id && context.kept?.includes(f.id)));
  // F42 (Fabi, Phase Kern): Wer gefallen ist, kommt verwundet zurück
  const wounded = r.friends.filter((f) => f.fell && !f.farewell);
  const hurt = r.friends.filter((f) => !f.farewell && !f.fell && f.hpEnd / f.maxHp < 0.34);
  const notes: string[] = [];
  if (gone.length) notes.push(`${gone.map((f) => esc(f.name)).join(', ')} ${gone.length === 1 ? 'nimmt' : 'nehmen'} Abschied.`);
  if (wounded.length) notes.push(`${wounded.map((f) => esc(f.name)).join(', ')} ${wounded.length === 1 ? 'ist' : 'sind'} gefallen und ${wounded.length === 1 ? 'kommt' : 'kommen'} verwundet zurück. Fällt ein Verwundeter vor der Nacht noch einmal, nimmt er Abschied.`);
  if (hurt.length) notes.push(`Schwer angeschlagen: ${hurt.map((f) => esc(f.name)).join(', ')}.`);
  // K21 (Fabi): Wer über den Rand geschubst wurde, wartet dort und kommt nach dem Kampf wieder an Bord
  const pushed = r.friends.filter((f) => f.pushedOut);
  if (pushed.length) notes.push(`${pushed.map((f) => esc(f.name)).join(', ')} ${pushed.length === 1 ? 'wurde' : 'wurden'} über den Rand geschubst und ${pushed.length === 1 ? 'kommt' : 'kommen'} nach dem Kampf wieder an Bord.`);
  if (!gone.length && !hurt.length && !wounded.length && r.outcome === 'win') notes.push('Alle kommen gut zurück.');
  // Spieltest Phase 4 (Fabi): Bekommen mehrere gleich viel, stehen alle da
  const standing = r.friends.filter((f) => !f.farewell);
  const best = Math.max(0, ...standing.map(xpOf));
  const tops = standing.filter((f) => xpOf(f) === best);
  if (tops.length && best > 0 && r.outcome === 'win') {
    const names = tops.map((f) => esc(f.name));
    const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} und ${names[names.length - 1]}` : names[0];
    notes.push(`Die meisten XP ${tops.length > 1 ? 'bekommen' : 'bekommt'} ${list}.`);
  }
  const stormName = combatRules.stormLevels.find((l) => l.id === context.storm)?.name ?? context.storm;

  // Spieltest Phase 4 (Fabi): Im Run baut sich die Übersicht von oben nach unten auf: Beute, XP, dann die Zahlen
  return `<div class="summary-card${context.extra ? ' staged' : ''}">
    <h2>${title}</h2>
    <p class="sub">${sub}</p>
    <p class="meta">${esc(context.lineup)} · Sturm: ${esc(stormName)} · ${r.duration.toFixed(1).replace('.', ',')} s · XP-Topf ${n(r.xpPot)}</p>
    ${r.escaped ? `<p class="lost-xp">${n(r.xpLostToEscapes)} XP verloren, weil ${r.escaped} ${r.escaped === 1 ? 'Gegner entkommen ist' : 'Gegner entkommen sind'}. Ohne Flucht wären es ${n(r.xpPot + r.xpLostToEscapes)} gewesen.</p>` : ''}
    <p class="notes">${notes.join(' ')}</p>
    ${context.extra ?? ''}
    <div class="table-wrap reveal r3"><table>
      <thead><tr><th>Freund</th><th>Leben danach</th><th>Schaden</th><th>Eingesteckt</th><th>Geheilt</th><th>Geschützt</th><th>Besiegt</th><th>Volltreffer</th><th>XP</th></tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
    <h3 class="enemies-title reveal r3">Die Gegner</h3>
    <div class="table-wrap reveal r3"><table class="enemies">
      <thead><tr><th>Gegner</th><th>Leben danach</th><th>Schaden</th><th>Eingesteckt</th><th>Volltreffer</th></tr></thead>
      <tbody>${enemyRows(r, context.crits)}</tbody>
    </table></div>
    <div class="actions">
      <button id="again" class="primary">${context.againLabel ?? 'Neuer Versuch'} <kbd class="pad">A</kbd></button>
      <button id="replay">Kampf nochmal ansehen <kbd class="pad">B</kbd></button>
    </div>
    <div id="summary-log" class="summary-log"></div>
    <p class="fine">${context.fine ?? 'Klickt auf eine Zeile für den Verlauf.'}</p>
  </div>`;
}

/**
 * Gleiche Sturmwesen auseinanderhalten. G30 (Fabi, Phase Kern, 17.10.2026): Jedes Sturmwesen heißt nur mit seinem
 * Spitznamen, etwa „Klecks“ und „Batz“; die Spezies (Dreckschleuder) steht beim Darüberfahren und Anklicken. Ohne
 * Spitznamen, oder wenn sie ausgehen, heißt es wie seine Spezies, bei mehreren mit Nummer
 */
export function enemyNames(ids: string[]): string[] {
  const total = new Map<string, number>();
  for (const id of ids) total.set(id, (total.get(id) ?? 0) + 1);
  const seen = new Map<string, number>();
  return ids.map((id) => {
    const n = (seen.get(id) ?? 0) + 1;
    seen.set(id, n);
    const def = getEnemy(id);
    const nick = def.nicknames?.[n - 1];
    if (nick) return nick;
    return total.get(id)! <= 1 ? def.name : `${def.name} ${n}`;
  });
}

/** Spitzname mit Spezies, für Listen außerhalb des Felds: „Klecks (Dreckschleuder)“ */
export function enemyLabel(nick: string, id: string): string {
  const def = getEnemy(id);
  return nick.startsWith(def.name) ? nick : `${nick} (${def.name})`;
}

/** Was die Gegner ausgeteilt und eingesteckt haben (Fabi, Phase 3) */
function enemyRows(r: BattleResult, crits: Map<number, number>): string {
  const enemies = r.units.filter((u) => u.side === 'enemy' && u.kind !== 'tree');
  const storm = enemyNames(enemies.filter((u) => u.kind === 'stormling').map((u) => u.defId));
  let si = 0;
  const names = enemies.map((u) => (u.kind === 'infested' ? `${u.name} (befallen)` : storm[si++]!));
  // G30: neben dem Spitznamen klein die Spezies
  const kinds = enemies.map((u, i) => (u.kind === 'stormling' && !names[i]!.startsWith(getEnemy(u.defId).name) ? getEnemy(u.defId).name : ''));
  return enemies
    .map((u, i) => {
      const share = u.hp / u.maxHp;
      const traits = u.traits.map((t) => getTrait(t).name).join(', ');
      return `<tr class="${u.alive ? '' : 'farewell'}" data-uid="${u.uid}">
        <th scope="row"><span class="dot" style="background:#6b6a8f"></span>${esc(names[i]!)}${kinds[i] || traits ? `<small>${esc([kinds[i], traits].filter(Boolean).join(' · '))}</small>` : ''}</th>
        <td class="hp ${u.alive ? (share < 0.34 ? 'low' : share < 0.67 ? 'mid' : 'high') : 'gone'}">${u.alive ? `<span class="hpbar"><span style="width:${share * 100}%"></span></span>${n(u.hp)} / ${n(u.maxHp)}` : u.escaped ? '<em class="escaped">entkommen</em>' : u.kind === 'infested' ? '<em>befreit</em>' : '<em>vertrieben</em>'}</td>
        <td>${n(u.stats.damage)}</td>
        <td>${n(u.stats.taken)}</td>
        <td>${crits.get(u.uid) ?? 0}</td>
      </tr>`;
    })
    .join('');
}

/** S5: Der Baum des Eichhörnchens hat sein eigenes Leben, das in den nächsten Kampf mitgeht (später alle Objekte) */
function treeRows(r: BattleResult): string {
  return r.units
    .filter((u) => u.kind === 'tree')
    .map((u) => {
      const share = Math.max(0, u.hp) / u.maxHp;
      return `<tr class="prop ${u.alive ? '' : 'farewell'}" data-uid="${u.uid}">
        <th scope="row"><span class="dot" style="background:#4f7a4a"></span>${esc(u.name)}<small>Objekt</small></th>
        <td class="hp ${u.alive ? (share < 0.34 ? 'low' : share < 0.67 ? 'mid' : 'high') : 'gone'}">${u.alive ? `<span class="hpbar"><span style="width:${share * 100}%"></span></span>${n(u.hp)} / ${n(u.maxHp)}` : '<em>gefällt</em>'}</td>
        <td>–</td><td>${n(u.stats.taken)}</td><td>–</td><td>–</td><td>–</td><td>–</td><td>–</td>
      </tr>`;
    })
    .join('');
}
